import getPhoneNumberSearchablePart from 'utils/phone-number/get-phone-number-searchable-part';

const FILTER_NULL = 'null';
const FILTER_NOT_NULL = 'notnull';

export type BackOfficeRequestParamsInput = {
  currentPage?: number;
  search?: string;
  sortField?: string;
  sortOrder?: string;
  filterSet: Record<string, string[]>;
};

export function buildBackOfficeEnterprisesRequestParams({
  currentPage,
  search,
  sortField,
  sortOrder,
  filterSet
}: BackOfficeRequestParamsInput) {
  const filterSetKeys = Object.keys(filterSet);
  return {
    per_page: 20,
    page: currentPage || 1,
    sort: [
      `${sortField || 'name'},${sortOrder === 'descend' ? 'DESC' : 'ASC'}`,
      ...(sortField !== 'id' ? ['id,ASC'] : [])
    ],
    ...(search || filterSetKeys.length
      ? {
        s: {
          $and: [
            ...(search
              ? [{ $or: [{ name: { $contL: search } }] }]
              : []),
            ...(filterSetKeys.length
              ? filterSetKeys.map((filterSetKey) => ({
                [filterSetKey]: { $in: filterSet[filterSetKey] }
              }))
              : [])
          ]
        }
      }
      : {})
  };
}

export function buildBackOfficeUsersRequestParams({
  currentPage,
  search,
  sortField,
  sortOrder,
  filterSet
}: BackOfficeRequestParamsInput) {
  const keywords = getPhoneNumberSearchablePart(search); // If it's not a phone number, the string won't change.
  const filterSetKeys = Object.keys(filterSet);
  return {
    per_page: 20,
    page: currentPage || 1,
    sort: [
      `${sortField || 'firstName'},${sortOrder === 'descend' ? 'DESC' : 'ASC'}`,
      ...(sortField !== 'id' ? ['id,ASC'] : [])
    ],
    join: ['memberships'],
    ...(keywords || filterSetKeys.length
      ? {
        s: {
          $and: [
            ...(keywords
              ? // Split search by spaces and search for each part
              // individually. This allows a user to enter a full name e.g.,
              // 'John Citizen' and return results.
              keywords
                .split(' ')
                .filter((s) => !!s)
                .map((keyword) => ({
                  $or: [
                    { firstName: { $contL: keyword } },
                    { lastName: { $contL: keyword } },
                    { email: { $contL: keyword } },
                    { username: { $contL: keyword } },
                    { mobileNumber: { $contL: keyword } }
                  ]
                }))
              : []),
            ...(filterSetKeys.length
              ? filterSetKeys.map((filterSetKey) => ({
                [filterSetKey]: { $in: filterSet[filterSetKey] }
              }))
              : [])
          ]
        }
      }
      : {})
  };
}

export function buildBackOfficeDevicesRequestParams({
  currentPage,
  search,
  sortField,
  sortOrder,
  filterSet
}: BackOfficeRequestParamsInput) {
  const filterSetKeys = Object.keys(filterSet);
  return {
    per_page: 20,
    page: currentPage || 1,
    sort: sortField
      ? [`${sortField},${sortOrder === 'descend' ? 'DESC' : 'ASC'}`]
      : [],
    ...(search || filterSetKeys.length
      ? {
        s: {
          $and: [
            ...(search
              ? [
                {
                  $or: [
                    { name: { $contL: search } },
                    { serialNumber: { $contL: search } }
                  ]
                }
              ]
              : []),
            ...(filterSetKeys.length
              ? filterSetKeys.map((filterSetKey) => {
                const values = filterSet[filterSetKey];
                // Allow filtering by 'null' in commit: https://bitbucket.org/farmbotteam/farmbot-web-app/commits/e1cc1bf1d136d96bde04e9254c2582ab34d19776
                const hasNull = values.includes(FILTER_NULL);
                // Allow filtering by 'notnull' in commit: https://bitbucket.org/farmbotteam/farmbot-web-app/commits/105cc763cb7821f8ec1640dd4db36ecad361a81a
                const hasNotNull = values.includes(FILTER_NOT_NULL);
                /**
                 * If the only filter condition is 'null'/'notnull', return empty object.
                 *
                 * Else, return object with all filter values except 'null'/'notnull'.
                 */
                const conditions =
                  (hasNull || hasNotNull) && values.length === 1
                    ? {}
                    : {
                      $in:
                        hasNull || hasNotNull
                          ? values.filter((value) => value !== FILTER_NULL && value !== FILTER_NOT_NULL)
                          : values
                    };
                return {
                  [filterSetKey]:
                    // Don't support filtering by both 'null' & 'not null' simultaneously.
                    hasNull || hasNotNull
                      ? {
                        [hasNull ? '$isnull' : '$notnull']: true,
                        ...conditions
                      }
                      : conditions
                };
              })
              : [])
          ]
        }
      }
      : {})
  };
}

export function buildBackOfficeInvitationsRequestParams({
  currentPage,
  search,
  sortField,
  sortOrder,
  filterSet
}: BackOfficeRequestParamsInput) {
  const filterSetKeys = Object.keys(filterSet);
  const sort: string[] = [];
  const defaultSortParam = 'lastInviteDate,DESC';

  if (sortField) {
    if (sortField === 'lastInviteDate') {
      sort.push(`lastInviteDate,${sortOrder === 'descend' ? 'DESC' : 'ASC'}`);
    } else {
      sort.push(
        `${sortField},${sortOrder === 'descend' ? 'DESC' : 'ASC'}`,
        defaultSortParam
      );
    }
  } else {
    sort.push(defaultSortParam);
  }

  return {
    per_page: 20,
    page: currentPage || 1,
    sort,
    ...(search || filterSetKeys.length
      ? {
        s: {
          $and: [
            ...(search ? [{ $or: [{ email: { $contL: search } }] }] : []),
            ...(filterSetKeys.length
              ? filterSetKeys.map((filterSetKey) => ({
                [filterSetKey]: { $in: filterSet[filterSetKey] }
              }))
              : [])
          ]
        }
      }
      : {})
  };
}
